// Where the person was going before they were asked to sign in.
//
// This exists because of one broken path: an invitation link, `/join?token=…`,
// clicked by someone who is not signed in. The gate sent them to /signin and
// the token went with the old URL, so signing in landed them in their own
// workspace with no sign that an invitation had ever been waiting.
//
// The destination now travels as `?next=`, and everything that reads it treats
// it as hostile input. It ends up in `emailRedirectTo`, which is a URL a
// stranger could otherwise use this app to point at their own site.

/**
 * A `next` value that can only ever mean "somewhere in this app".
 *
 * Same-origin is not enough, and neither is "starts with /": `//evil.example`
 * is a protocol-relative URL that browsers resolve to another host, and
 * `/\evil.example` is treated the same way by some of them. Anything that is
 * not a plain in-app path is dropped rather than repaired.
 */
export function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
  // A second scheme, a host, or a newline smuggled in through an encoded value.
  if (/[\r\n]/.test(raw) || raw.includes("://")) return null;
  return raw;
}

/** The current location as a `next` value: path, query and nothing else. */
export function currentNext(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`;
}

/** `/signin?next=…`, or plain `/signin` when there is nothing worth keeping. */
export function signInWithNext(next: string | null, base = "/signin"): string {
  const safe = safeNext(next);
  // The app's own front door is not a destination worth remembering.
  if (!safe || safe === "/" || safe.startsWith("/signin")) return base;
  return `${base}?next=${encodeURIComponent(safe)}`;
}
